import React, {
  useCallback, useEffect, useRef, useState,
} from 'react';

import {
  LocationNavigationIcon, MaximizeIcon, MinusIcon, PlusIcon,
} from '@vlabs/icons';
import { ButtonScrimGroup } from '@vlabs/uikit';
import { IconButton } from '@vlabs/uikit/components/controls/button/icon-button/IconButton';
import cn from 'classnames';
import type { Map as TMap } from 'ol';
import Feature from 'ol/Feature';
import Polygon from 'ol/geom/Polygon';
import Draw, { createBox } from 'ol/interaction/Draw';
import TileLayer from 'ol/layer/Tile';
import VectorLayer from 'ol/layer/Vector';
import Map from 'ol/Map';
import { fromLonLat, toLonLat } from 'ol/proj';
import OSM from 'ol/source/OSM';
import VectorSource from 'ol/source/Vector';
import View from 'ol/View';
import { createPortal } from 'react-dom';
import { toast } from 'react-toastify';

import st from './ControlledMap.module.sass';
import { useControlledMapTranslation } from './i18n';

export interface ControlledMapCoordinates {
  latitude_delta?: unknown;
  longitude_delta?: unknown;
  origin_latitude?: unknown;
  origin_longitude?: unknown;
}

interface ControlledMapProps {
  id: string;
  isFullScreenEnabled?: boolean;
  onChange: (value: ControlledMapCoordinates) => void;
  value: ControlledMapCoordinates;
}

type MapAction = 'increase' | 'decrease' | 'geolocation';

const defaultCenter = [4187399, 7508881];
const defaultZoom = 10;

function toNumber(value: unknown): number | undefined {
  if (value === '' || value === null || value === undefined) return undefined;
  const result = Number(value);
  return Number.isNaN(result) ? undefined : result;
}

function ControlledMap({
  id,
  isFullScreenEnabled = false,
  onChange,
  value,
}: ControlledMapProps): React.JSX.Element {
  const { t } = useControlledMapTranslation();
  const [isFullScreen, setIsFullScreen] = useState(false);
  const mapTargetElement = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<TMap | null>(null);
  const onChangeRef = useRef(onChange);

  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  const getUserLocation = useCallback(async () => new Promise<GeolocationPosition | null>((resolve) => {
    if (!navigator.geolocation) {
      toast.error(t('location_unsupported'));
      resolve(null);
      return;
    }
    navigator.geolocation.getCurrentPosition(
      resolve,
      (error) => {
        console.warn('Geolocation error:', error);
        toast.error(t('location_unavailable'));
        resolve(null);
      },
      { enableHighAccuracy: true, timeout: 5000 },
    );
  }), [t]);

  const changeMapView = async (action: MapAction) => {
    const view = mapRef.current?.getView();
    const currentCenter = view?.getCenter();
    const currentZoom = view?.getZoom();
    if (!view || !currentCenter || currentZoom === undefined) return;

    if (action === 'increase') view.setZoom(currentZoom + 1);
    if (action === 'decrease') view.setZoom(currentZoom - 1);
    if (action === 'geolocation') {
      const location = await getUserLocation();
      if (!location) {
        view.setCenter(defaultCenter);
        view.setZoom(defaultZoom);
        return;
      }
      view.setCenter(fromLonLat([location.coords.longitude, location.coords.latitude]));
      view.setZoom(14);
    }
  };

  useEffect(() => {
    if (mapRef.current) return undefined;
    const source = new VectorSource({ wrapX: false });
    const map = new Map({
      controls: [],
      layers: [
        new TileLayer({ source: new OSM() }),
        new VectorLayer({ source }),
      ],
      target: mapTargetElement.current ?? '',
      view: new View({ center: defaultCenter, zoom: defaultZoom }),
    });
    const draw = new Draw({
      geometryFunction: createBox(),
      source,
      type: 'Circle',
    });
    map.addInteraction(draw);
    mapRef.current = map;

    const latitude = toNumber(value.origin_latitude);
    const longitude = toNumber(value.origin_longitude);
    const latitudeDelta = toNumber(value.latitude_delta);
    const longitudeDelta = toNumber(value.longitude_delta);
    if (
      latitude !== undefined
      && longitude !== undefined
      && latitudeDelta !== undefined
      && longitudeDelta !== undefined
    ) {
      source.addFeature(new Feature(new Polygon([[
        fromLonLat([longitude - longitudeDelta, latitude - latitudeDelta]),
        fromLonLat([longitude - longitudeDelta, latitude + latitudeDelta]),
        fromLonLat([longitude + longitudeDelta, latitude + latitudeDelta]),
        fromLonLat([longitude + longitudeDelta, latitude - latitudeDelta]),
        fromLonLat([longitude - longitudeDelta, latitude - latitudeDelta]),
      ]])));
    }

    draw.on('drawstart', () => source.clear());
    source.on('addfeature', (event) => {
      const coordinates = event.feature?.getGeometry();
      if (!(coordinates instanceof Polygon)) return;
      const points = coordinates.getCoordinates()[0];
      const [longitude1, latitude1] = toLonLat(points[0]);
      const [longitude2, latitude2] = toLonLat(points[2]);
      const nextLongitudeDelta = (longitude2 - longitude1) / 2;
      const nextLatitudeDelta = (latitude2 - latitude1) / 2;
      onChangeRef.current({
        latitude_delta: nextLatitudeDelta,
        longitude_delta: nextLongitudeDelta,
        origin_latitude: latitude1 + nextLatitudeDelta,
        origin_longitude: longitude1 + nextLongitudeDelta,
      });
    });

    return () => map.setTarget('');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const target = isFullScreen ? document.getElementById(id) : mapTargetElement.current;
    if (!target || !mapRef.current) return;
    mapRef.current.setTarget(target);
    requestAnimationFrame(() => mapRef.current?.updateSize());
  }, [id, isFullScreen]);

  const mapContent = (
    <React.Fragment>
      {!isFullScreen && <div className={st.Title}>{t('select_area')}</div>}
      <div className={cn(st.Wrapper, { [st.FullScreen]: isFullScreen })}>
        {!!isFullScreenEnabled && (
          <ButtonScrimGroup
            className={cn(st.Button, st.MaximizeButton)}
            orientation="vertical"
          >
            <IconButton
              icon={<MaximizeIcon />}
              onClick={() => setIsFullScreen((current) => !current)}
            />
          </ButtonScrimGroup>
        )}
        <ButtonScrimGroup
          className={cn(st.Button, st.ZoomButtons)}
          orientation="vertical"
        >
          <IconButton
            icon={<PlusIcon />}
            onClick={async () => changeMapView('increase')}
          />
          <IconButton
            icon={<MinusIcon />}
            onClick={async () => changeMapView('decrease')}
          />
        </ButtonScrimGroup>
        <ButtonScrimGroup
          className={cn(st.Button, st.GeoLocationButton)}
          orientation="vertical"
        >
          <IconButton
            icon={<LocationNavigationIcon />}
            onClick={async () => changeMapView('geolocation')}
          />
        </ButtonScrimGroup>
        <div
          className={st.Map}
          id={id}
          ref={mapTargetElement}
        />
      </div>
    </React.Fragment>
  );

  if (isFullScreen) {
    const root = document.getElementById('root');
    if (root) return createPortal(mapContent, root);
  }
  return mapContent;
}

export { ControlledMap };
